// src/component/Navbar/index.jsx

import React, { useState, useEffect, useContext } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Badge from "@mui/material/Badge";
import Hidden from "@mui/material/Hidden";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../../Context";
import { ThemeModeContext } from "../../theme/ThemeProvider";
import { axiosInstance } from "../Api/instance";
import { auth } from "../../FirebaseConfig";
import Logo from "../../asserts/Artboard 2.png";
import { PrimaryButton, SecondaryButton } from "../ui/Buttons";
import { styled } from "@mui/material/styles";

// Premium Glassmorphism Navbar styles
const GlassAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(11, 15, 25, 0.8)' : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(12px)',
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  top: 0,
  zIndex: theme.zIndex.drawer + 1,
}));

const LogoLink = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  gap: '12px',
});

const NavMenu = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  marginRight: 'auto',
  marginLeft: '40px',
}));

const NavItemLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.text.secondary,
  fontWeight: 600,
  fontSize: '0.925rem',
  padding: '6px 0',
  position: 'relative',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: theme.palette.text.primary,
  },
  '&.active': {
    color: theme.palette.primary.main,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '2px',
      backgroundColor: theme.palette.primary.main,
      borderRadius: '2px',
    },
  },
}));

const PopoverMenu = styled(Box)(({ theme }) => ({
  padding: '16px',
  minWidth: '200px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  backgroundColor: theme.palette.background.paper,
}));

const NotificationItem = styled(ListItem)(({ theme }) => ({
  borderRadius: '12px',
  margin: '4px 16px',
  width: 'calc(100% - 32px)',
  transition: 'background-color 0.15s ease',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const Navbar = () => {
  const [handleopen, setHandleopen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [newNotificationCount, setNewNotificationCount] = useState(0);
  const { Endpoint } = useContext(UserContext);
  const { mode, toggleThemeMode } = useContext(ThemeModeContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const email = localStorage.getItem("userMail");
  const username = localStorage.getItem("username") || "Student";
  const userProfile = localStorage.getItem("profile");
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(()=>{
    setAnchorEl(null)
  },[location.pathname])

  useEffect(() => {
    const fetchData = async () => {
      if (!email) return;
      try {
        const response = await axiosInstance.post(
          `get/U_ViewNotification.php`,
          {
            userId: email,
          }
        );
        const data = Array.isArray(response.data) ? response.data : [];
        setNotifications(data);
        calculateNewNotifications(data);
      } catch (error) {
        console.error("Error fetching data:", error);
        setNotifications([]);
      }
    };

    fetchData();
  }, [email, Endpoint]);

  const calculateNewNotifications = (notifications) => {
    const today = new Date().toISOString().split("T")[0];
    const count = notifications.filter(
      (notification) =>
        new Date(notification.created_at).toISOString().split("T")[0] === today
    ).length;
    setNewNotificationCount(count);
  };

  const handleClickOpens = () => {
    try {
      localStorage.removeItem("userMail");
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      localStorage.removeItem("profile");
      auth.signOut();
      navigate("/signin");
    } catch (e) {
      console.error(e);
    }
  };

  const handleClickOpen = () => {
    setHandleopen(true);
    setNewNotificationCount(0);
  };

  const handleClose = () => {
    setHandleopen(false);
  };

  const handlepopClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlepopClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "profile-popover" : undefined;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleNotificationClick = (notification) => {
    const { content } = notification;

    if (content.includes("Daily Test")) {
      setHandleopen(false);
      navigate("/dailyinstruction");
    } else if (content.includes("Mini Test")) {
      setHandleopen(false);
      navigate("/miniinstruction");
    } else if (content.includes("Micro Test")) {
      setHandleopen(false);
      navigate("/microinstruction");
    } else if (content.includes("Subject")) {
      setHandleopen(false);
      navigate("/subject");
    } else if (content.includes("Non Nursing")) {
      setHandleopen(false);
      navigate("/non_nursing");
    } else if (content.includes("Model Mock")) {
      setHandleopen(false);
      navigate("/model");
    } else if (content.includes("Prelims")) {
      setHandleopen(false);
      navigate("/prelims");
    } else if (content.includes("Mains")) {
      setHandleopen(false);
      navigate("/mains");
    }
  };

  const isPublicPage = [
    "/",
    "/signup",
    "/signin",
    "/otp",
    "/forget"
  ].includes(location.pathname);

  return (
    <GlassAppBar position="sticky">
      <Toolbar style={{ justifyContent: "space-between", minHeight: "72px" }}>
        
        {/* Brand Logo & Name */}
        <Box style={{ display: "flex", alignItems: "center" }}>
          {!isPublicPage && (
            <Hidden mdUp>
              <IconButton
                color="inherit"
                onClick={toggleDrawer}
                edge="start"
                style={{ marginRight: "8px", color: "var(--color-text-primary)" }}
              >
                <MenuIcon />
              </IconButton>
            </Hidden>
          )}
          
          <LogoLink to={isPublicPage ? "/" : "/home"}>
            <img
              src={Logo}
              alt=""
              height="40px"
              width="40px"
              style={{ objectFit: "contain" }}
            />
            <Typography
              variant="h3"
              style={{
                color: "var(--color-text-primary)",
                fontWeight: "750",
                letterSpacing: "-0.01em",
              }}
            >
              NURSING UPDATE
            </Typography>
          </LogoLink>
        </Box>

        {/* Desktop Menu Links */}
        {!isPublicPage && (
          <Hidden mdDown>
            <NavMenu>
              <NavItemLink to="/home">Home</NavItemLink>
              <NavItemLink to="/courses">Courses</NavItemLink>
              <NavItemLink to="/about">About Us</NavItemLink>
              <NavItemLink to="/contact">Contact Us</NavItemLink>
            </NavMenu>
          </Hidden>
        )}

        {/* Right Section Actions */}
        <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          
          {/* Light/Dark mode toggler */}
          <IconButton onClick={toggleThemeMode} color="inherit" style={{ color: "var(--color-text-primary)" }}>
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>

          {isPublicPage ? (
            location.pathname === "/signin" ? (
              <PrimaryButton 
                onClick={() => navigate("/signup")}
                style={{ height: "40px", minWidth: "90px" }}
              >
                Sign Up
              </PrimaryButton>
            ) : (
              <PrimaryButton 
                onClick={() => navigate("/signin")}
                style={{ height: "40px", minWidth: "90px" }}
              >
                Sign In
              </PrimaryButton>
            )
          ) : (
            <>
              {/* Notification icon */}
              <IconButton onClick={handleClickOpen} color="inherit" style={{ color: "var(--color-text-primary)" }}>
                <Badge badgeContent={newNotificationCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              {/* Profile avatar link */}
              <IconButton 
                onClick={handlepopClick} 
                aria-describedby={id}
                style={{ padding: "4px" }}
              >
                {userProfile ? (
                  <img
                    src={userProfile}
                    alt={username}
                    style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover", border: "2px solid var(--color-primary)" }}
                  />
                ) : (
                  <AccountCircleIcon fontSize="large" style={{ color: "var(--color-text-primary)" }} />
                )}
              </IconButton>

              {/* Profile dropdown popover */}
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handlepopClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  style: { borderRadius: "16px", marginTop: "8px", boxShadow: "var(--color-shadow-hover)" }
                }}
              >
                <PopoverMenu>
                  <Box style={{ paddingBottom: "8px", borderBottom: "1px solid var(--color-border)" }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                      {username}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {email}
                    </Typography>
                  </Box>
                  
                  <ListItemButton 
                    onClick={() => { handlepopClose(); navigate("/profile"); }}
                    style={{ borderRadius: "10px", padding: "8px 12px" }}
                  >
                    <ListItemText primary="My Profile" />
                  </ListItemButton>
                  
                  <SecondaryButton 
                    onClick={handleClickOpens}
                    startIcon={<LogoutIcon />}
                    style={{ width: "100%", height: "38px", borderRadius: "12px" }}
                  >
                    Logout
                  </SecondaryButton>
                </PopoverMenu>
              </Popover>
            </>
          )}
        </Box>
      </Toolbar>

      {/* Mobile Drawer Navigation */}
      {!isPublicPage && (
        <Drawer 
          anchor="left" 
          open={isDrawerOpen} 
          onClose={toggleDrawer}
          PaperProps={{
            style: { width: "260px", backgroundColor: "var(--color-paper)", padding: "16px" }
          }}
        >
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", paddingBottom: "16px", borderBottom: "1px solid var(--color-border)" }}>
            <Typography variant="h3" style={{ fontWeight: 800 }}>
              Menu
            </Typography>
            <IconButton onClick={toggleDrawer} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <List>
            <ListItem disablePadding style={{ marginBottom: "8px" }}>
              <ListItemButton 
                component={NavLink} 
                to="/home" 
                onClick={toggleDrawer}
                style={{ borderRadius: "12px" }}
              >
                <ListItemText primary="Home" />
                <ArrowForwardIosIcon fontSize="inherit" color="action" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding style={{ marginBottom: "8px" }}>
              <ListItemButton 
                component={NavLink} 
                to="/courses" 
                onClick={toggleDrawer}
                style={{ borderRadius: "12px" }}
              >
                <ListItemText primary="Courses" />
                <ArrowForwardIosIcon fontSize="inherit" color="action" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding style={{ marginBottom: "8px" }}>
              <ListItemButton 
                component={NavLink} 
                to="/about" 
                onClick={toggleDrawer}
                style={{ borderRadius: "12px" }}
              >
                <ListItemText primary="About" />
                <ArrowForwardIosIcon fontSize="inherit" color="action" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding style={{ marginBottom: "8px" }}>
              <ListItemButton 
                component={NavLink} 
                to="/contact" 
                onClick={toggleDrawer}
                style={{ borderRadius: "12px" }}
              >
                <ListItemText primary="Contact us" />
                <ArrowForwardIosIcon fontSize="inherit" color="action" />
              </ListItemButton>
            </ListItem>
            
            <ListItem disablePadding style={{ marginBottom: "8px" }}>
              <ListItemButton 
                component={NavLink} 
                to="/profile" 
                onClick={toggleDrawer}
                style={{ borderRadius: "12px" }}
              >
                <ListItemText primary="Profile" />
                <ArrowForwardIosIcon fontSize="inherit" color="action" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding style={{ marginTop: "24px", borderTop: "1px solid var(--color-border)", paddingTop: "16px" }}>
              <SecondaryButton 
                onClick={() => { toggleDrawer(); handleClickOpens(); }}
                startIcon={<LogoutIcon />}
                style={{ width: "100%", height: "42px", borderRadius: "14px" }}
              >
                Logout
              </SecondaryButton>
            </ListItem>
          </List>
        </Drawer>
      )}

      {/* Notifications Dialog */}
      <Dialog
        open={handleopen}
        onClose={handleClose}
        aria-labelledby="notification-dialog-title"
        PaperProps={{
          style: { borderRadius: "24px", maxWidth: "500px", width: "100%" }
        }}
      >
        <DialogTitle id="notification-dialog-title" style={{ padding: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--color-border)" }}>
          <Typography variant="h2" style={{ fontWeight: 800 }}>
            Notifications
          </Typography>
          <IconButton onClick={handleClose} size="small" style={{ border: "1px solid var(--color-border)", borderRadius: "10px" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <List style={{ padding: "16px 0", maxHeight: "400px", overflowY: "auto" }}>
          {notifications.length === 0 ? (
            <Box style={{ padding: "32px", textAlign: "center" }}>
              <Typography variant="body1" color="textSecondary">
                No notifications found.
              </Typography>
            </Box>
          ) : (
            notifications.map((notification, index) => (
              <NotificationItem
                key={index}
                button
                onClick={() => handleNotificationClick(notification)}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle1" style={{ fontWeight: 700, color: "var(--color-primary)" }}>
                      {notification.title}
                    </Typography>
                  }
                  secondary={
                    <Box style={{ marginTop: "6px" }}>
                      <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: "4px" }}>
                        {notification.date}
                      </Typography>
                      <Typography variant="body1" color="textPrimary">
                        {notification.content}
                      </Typography>
                    </Box>
                  }
                />
              </NotificationItem>
            ))
          )}
        </List>
      </Dialog>
    </GlassAppBar>
  );
};

export default Navbar;
